import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FilterQuery, YesObject } from '@yes.technology/react-toolkit'

import resolveCardConfiguration from 'component/AMFTaskObjectCard/helpers/resolveCardConfiguration'
import { useObjectclass } from 'objectclass/shared'
import { useSitemodel } from 'sitemodel/shared/hooks'
import { useSearchParams } from 'react-router-dom'
import { useBreakpoint } from 'shared/hooks/useBreakpoint'
import { updateDisplayText } from 'shared/utils'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import useMergedFilterQuery from 'site/shared/hooks/useMergedFilterQuery'
import { useSiteState } from 'siteState/shared'
import { useFetchYesObjects } from 'yesObject/shared'
import useClassification from '../DataCards/hooks/useClassification'
import { KanbanMainContainer, SIZE_MAP } from './Kanban.styles'
import { KanbanCardsProps } from './Kanban.types'
import KanbanAreaLine from './components/KanbanAreaLine'
import KanbanAutoColumns from './components/KanbanAutoColumns'
import KanbanSkeleton from './components/KanbanSkeleton'
import {
  getNewTaskColumnIndex,
  getSectionsByLayout,
  groupByCode,
  updateTaskColumn
} from './helpers'
import useAssociatedUsersRelations from './hooks/useAssociatedUserRelations'
import useRelationObjects from './hooks/useRelationObjects'
import useTasksByProcess from './hooks/useTasksByProcess'

const EMPTY_OBJECTS = [] as YesObject[]

function Kanban({ props }: KanbanCardsProps) {
  const {
    uuid_objectclass: siteModelObjectclassUuid,
    uuid_statusaction_classification: statusActionClassificationUuid,
    card_model: cardModel,
    group_by: groupByField,
    return_on_finished_interaction: returnOnFinishedInteraction,
    associated_users_relations: associatedRelations,
    layout,
    styleMap,
    objects = EMPTY_OBJECTS,
    visualizeUrl = '',
    orders_to_use,
    polling_interval,
    task_card_configuration: taskCardConfiguration
  } = props

  const [searchParams] = useSearchParams()

  const processUuid = searchParams.get('process-uuid') ?? ''
  const taskmodelUuid = searchParams.get('taskmodel-uuid') ?? ''

  const { tasks } = useTasksByProcess({ processUuid, taskmodelUuid })

  const styleProps = useStyleMaps(styleMap)

  const { classification: statusActionClassification } = useClassification({
    classificationUuid: statusActionClassificationUuid
  })

  const objectclassUuid = siteModelObjectclassUuid

  const sitemodel = useSitemodel()

  const { objectclass, isLoading: isLoadingObjectClass } = useObjectclass({
    objectclassUuid
  })

  const title = sitemodel?.title || objectclass?.des || ''

  const [filterQuery] = useSiteState<FilterQuery>('filter-query')

  const taskQuery: FilterQuery = useMemo(
    () =>
      tasks
        ? ({
            $or: tasks.map((item) => ({ uuid: { $eq: item.uuid } }))
          } as FilterQuery)
        : {},
    [tasks]
  )

  const query = useMergedFilterQuery({
    filterQuery,
    manifestFilterQuery: taskQuery
  })

  const { yesObjects, isLoading: isLoadingObjects } = useFetchYesObjects({
    initialSortOptions: layout?.order_by,
    initialPagination: {},
    filterBody: {
      query: query as FilterQuery,
      objectclass: objectclassUuid,
      skip_relations: false,
      skip_metadata: true
    },
    shouldMergeFilters: true,
    enabled: (!!filterQuery || !!tasks) && !!objectclassUuid,
    refetchInterval: polling_interval
  })

  const [mutableYesObjects, setMutableYesObjects] = useState<YesObject[]>([])

  const { relationObjects, isLoading: isLoadingFieldValues } =
    useRelationObjects({
      field_column: groupByField,
      objectclass: objectclass,
      objectclass_uuid: objectclassUuid
    })

  const breakpoint = useBreakpoint()
  const pageColumns = SIZE_MAP[cardModel][breakpoint]
  const [currentSection, setCurrentSection] = useState(0)

  const isLoading =
    isLoadingObjects || isLoadingObjectClass || isLoadingFieldValues

  useEffect(() => {
    // !!!: Breakpoint só pra evitar bugs com a mudança do tamanho
    setCurrentSection(0)
  }, [breakpoint, relationObjects])

  useEffect(() => {
    if (!!filterQuery && !!objectclassUuid) {
      setMutableYesObjects(yesObjects)
    }
  }, [filterQuery, objectclassUuid, yesObjects])

  useEffect(() => {
    if (!!objects && objects.length > 0) {
      setMutableYesObjects(objects)
    }
  }, [objects])

  useEffect(() => {
    if (tasks && tasks?.length > 0) {
      setMutableYesObjects(yesObjects)
    }
  }, [tasks, yesObjects])

  const groupedYesObjects = useMemo(
    () => groupByCode(relationObjects, mutableYesObjects, groupByField),
    [groupByField, mutableYesObjects, relationObjects]
  )

  const [customOrderedColumns, columnsOverrideAttributes] = useMemo(
    () => getSectionsByLayout(layout?.sections),
    [layout?.sections]
  )

  const orderedSections = customOrderedColumns || Object.keys(groupedYesObjects)

  const totalSections = orderedSections.length

  const fadeInResetTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(
    () => () => {
      if (fadeInResetTimeoutRef.current) {
        clearTimeout(fadeInResetTimeoutRef.current)
      }
    },
    []
  )

  const onMoveTask = useCallback(
    (
      taskUuid: string,
      newStatusColumn: string,
      setIsFadeInAnimationStarted: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
      const newTaskColumnIndex = getNewTaskColumnIndex({
        columns: orderedSections,
        newStatusColumn,
        pageColumns
      })

      if (newTaskColumnIndex >= 0) {
        setCurrentSection(newTaskColumnIndex)
      }

      setMutableYesObjects((prev) =>
        updateTaskColumn({
          objects: prev,
          newStatusColumn,
          taskUuid,
          groupByField
        })
      )

      setIsFadeInAnimationStarted(true)

      if (fadeInResetTimeoutRef.current) {
        clearTimeout(fadeInResetTimeoutRef.current)
      }

      fadeInResetTimeoutRef.current = setTimeout(() => {
        setIsFadeInAnimationStarted(false)
        fadeInResetTimeoutRef.current = undefined
      }, 2000)
    },
    [groupByField, orderedSections, pageColumns]
  )

  const totalNavigationPages = Math.ceil(totalSections / pageColumns)
  const shouldDisplayNavigation = !isLoadingObjects && totalNavigationPages > 0
  const { column_labels_text_fading: columnLabelsTextFading = true } =
    layout || {}

  const updatedFields = useMemo(
    () =>
      updateDisplayText(
        objectclass?.fields ?? {},
        taskCardConfiguration?.field_settings
      ),
    [objectclass?.fields, taskCardConfiguration?.field_settings]
  )

  const associatedUsersRelations = useAssociatedUsersRelations({
    associatedRelations,
    objectclassFields: updatedFields,
    taskCardConfiguration
  })

  const resolvedCardConfiguration = useMemo(
    () =>
      cardModel === 'AMFTaskObjectCard'
        ? resolveCardConfiguration(taskCardConfiguration, objectclass?.fields)
        : undefined,
    [cardModel, taskCardConfiguration, objectclass?.fields]
  )

  return (
    <KanbanMainContainer style={styleProps}>
      <KanbanAreaLine
        title={title}
        currentSection={currentSection}
        totalNavigationPages={totalNavigationPages}
        setCurrentSection={setCurrentSection}
        shouldDisplayNavigation={shouldDisplayNavigation}
      >
        {isLoading && <KanbanSkeleton pageColumns={pageColumns} />}

        {!isLoading && relationObjects.length > 0 && (
          <KanbanAutoColumns
            cardModel={cardModel}
            associatedUsersRelations={associatedUsersRelations}
            returnOnFinishedInteraction={returnOnFinishedInteraction}
            relationObjects={relationObjects}
            groupedYesObjects={groupedYesObjects}
            currentSection={currentSection}
            columnsPerPage={pageColumns}
            orderedSections={orderedSections}
            columnLabelsTextFading={columnLabelsTextFading}
            columnsOverrideAttributes={columnsOverrideAttributes}
            statusActionClassification={statusActionClassification}
            visualizeUrl={visualizeUrl}
            onMoveTask={onMoveTask}
            ordersToUse={orders_to_use}
            objectclassFields={updatedFields}
            resolvedCardConfiguration={resolvedCardConfiguration}
          />
        )}
      </KanbanAreaLine>
    </KanbanMainContainer>
  )
}

export default Kanban
